import React, { useEffect, useState } from "react";

import { classNames } from "../../../../helpers/classNames";
import Button from "../../../form/Button";
import Image from "../../../elements/Image";
import TextBlock from "../Common/TextBlock";
import useFetch from "../../../../hooks/useFetch";
import { projectList } from "../../../../services/projectService";
import {
  checkDateStatus,
  removeDuplicateFromArray,
  removeTag,
  replaceOldData,
} from "../../../../helpers";
import { useBanner } from "../../../../providers/BannerProviders";
import Quiltext from "../../../elements/Quiltext";
import Progress from "../../../elements/Progress";
import dayjs from "dayjs";

import SocialShare from "../../../elements/SocialShare";

const ProjectsItem = ({ data, index }) => {
  return (
    <section
      id={`project-${data?._id}`}
      className={classNames(
        "relative flex w-full py-16",
        index % 2 === 0 ? "bg-chestnut-rose/20" : "bg-[#C9C9C9]"
      )}
    >
      <div className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xls:max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl mx-auto px-4">
        <div
          className={classNames(
            "flex gap-16",
            index % 2 === 0 ? "flex-row" : "flex-row-reverse"
          )}
        >
          <div className="w-[45%] flex flex-shrink-0">
            <div className="w-full overflow-hidden">
              <Image
                src={data?.image}
                alt={""}
                effect="blur"
                classes="object-cover"
              />
            </div>
          </div>
          <div className="w-[55%] flex-shrink flex-grow flex flex-col space-y-12 py-12">
            <div className="space-y-4">
              {data?.name && (
                <h2 className="text-3xl sm:text-4xl xls:text-[42px] font-bold">
                  {data?.name}
                </h2>
              )}
              {data?.location && (
                <div className="text-lg font-Gotham font-medium">
                  <i className="fa-regular fa-location-dot"></i>{" "}
                  {data?.location}
                </div>
              )}
              {data?.time && (
                <div className="font-CanelaDeck text-lg gap-x-2 flex items-center whitespace-nowrap">
                  <i className="text-2xl fa-regular fa-fw fa-clock"></i>{" "}
                  {data?.time?.start} - {data?.time?.end}
                </div>
              )}
            </div>
            {data?.description && (
              <div>
                <Quiltext description={data?.description} />
              </div>
            )}
            {data?.metas?.length > 0 && (
              <div className="flex py-6 px-6 bg-white rounded-2xl divide-x divide-shades-05/50">
                {data?.metas?.map((item, index) => (
                  <div
                    key={index}
                    className="flex flex-col gap-2 items-center justify-center flex-shrink flex-grow"
                  >
                    <div className="text-3xl font-CanelaDeck font-bold">
                      {item?.type === "progress" ? (
                        <Progress
                          progress={item?.data}
                          className={"w-28 h-28"}
                          strokeWidth={8}
                        />
                      ) : (
                        item?.data
                      )}
                    </div>
                    <div className="text-xl font-medium text-chestnut-rose">
                      {item?.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-start gap-4 !mt-20">
              <Button
                buttonLabel={"Take Action"}
                buttonClasses={
                  "!h-12 xls:!h-14 !rounded-full !px-6 xls:!px-12 flex-grow"
                }
                buttonLabelClasses={"text-base xls:!text-xl"}
                buttonHasLink
                buttonLink={`/take-action?projectId=${data?._id}`}
              />
              <SocialShare data={data} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SectionAllProjects = ({ projectData }) => {
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const { data } = useBanner();

  useEffect(() => {
    if (projectData?.id) {
      setTimeout(() => {
        const element = document.getElementById(`project-${projectData?.id}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 1000);
    }
  }, [projectData?.id]);

  const projectBanner = data?.data?.find(
    (item) => item?.bannerType === "projects"
  );

  const {
    data: projectsData,
    error,
    isLoading,
  } = useFetch({
    dependencies: ["font-projects", filters],
    fetchFunction: async () => {
      const res = await projectList({
        ...filters,
      });
      if (res?.docs?.status === 200) {
        const projects = res?.docs?.data?.map((item) => {
          return {
            _id: item?._id,
            image: item?.image?.url,
            name: item?.title || "",
            time: {
              ...(item?.startDate && {
                start: dayjs(item?.startDate).format("MMMM DD, YYYY"),
              }),
              ...(item?.deadline
                ? {
                    end: dayjs(item?.deadline).format("MMMM DD, YYYY"),
                  }
                : { end: "Ongoing" }),
            },
            description: item?.description || "",
            metas: [
              {
                _id: 1,
                label: "Goal",
                data: item?.goalAmount || 0,
              },
              {
                _id: 2,
                label: "Amount Raised",
                data: item?.raisedAmount || 0,
              },
              {
                _id: 3,
                label: "Progress",
                type: "progress",
                data:
                  (
                    (Number(
                      item?.raisedAmount?.replace(/[^0-9.-]+/g, "") || 0
                    ) / Number(item?.goalAmount?.replace(/[^0-9.-]+/g, "")) ||
                      0) * 100
                  ).toFixed(1) || 0,
              },
            ],
          };
        });

        const newArr = replaceOldData(
          [...(projectsData?.data?.projects || [])],
          projects
        );

        return {
          ...res?.docs,
          data: {
            description: projectBanner?.contentDescription,
            projects: removeDuplicateFromArray([...newArr, ...projects]),
          },
        };
      } else {
        if (projectsData?.data?.length > 0) {
          return projectsData;
        } else {
          return res;
        }
      }
    },
  });

  const loadMoreProjects = (page) => {
    setFilters((pre) => ({ ...pre, page }));
  };

  return (
    <>
      <TextBlock description={projectsData?.data?.description} />
      {Array.isArray(projectsData?.data?.projects) &&
        projectsData?.data?.projects?.length > 0 &&
        projectsData?.data?.projects?.map((data, index) => (
          <ProjectsItem key={index} data={data} index={index} />
        ))}
      {projectsData?.metadata?.totalDocs > filters.limit &&
        projectsData?.metadata?.hasNextPage && (
          <section className="relative flex w-full py-12 bg-white">
            <div className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xls:max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl mx-auto px-4">
              <div className="flex items-center justify-center">
                <Button
                  buttonLabel={"Load More"}
                  buttonClasses={
                    "!h-12 xl:!h-14 !rounded-full !px-6 xl:!px-10 border border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white min-w-60"
                  }
                  buttonLabelClasses={"!text-base xl:!text-xl"}
                  buttonFunction={() =>
                    loadMoreProjects(projectsData?.metadata?.nextPage)
                  }
                />
              </div>
            </div>
          </section>
        )}
    </>
  );
};

export default SectionAllProjects;
